import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    AlertTriangle,
    CalendarCheck,
    CalendarClock,
    Link2,
    Play,
    RefreshCw,
    ShieldCheck,
    ShieldOff,
    Unlink,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const Reminders = () => {
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);
    const [running, setRunning] = useState(false);
    const [lastRun, setLastRun] = useState(null);
    const [google, setGoogle] = useState(null);
    const [calendars, setCalendars] = useState([]);
    const [gBusy, setGBusy] = useState(false);
    const [sp, setSp] = useSearchParams();

    const load = () =>
        Promise.all([api.get("/settings"), api.get("/google/status")])
            .then(([s, g]) => {
                setSettings(s.data);
                setGoogle(g.data);
                if (g.data.connected) {
                    api.get("/google/calendars").then((r) => setCalendars(r.data)).catch(() => {});
                }
            })
            .catch(() => toast.error("Failed to load settings"));

    useEffect(() => {
        load();
    }, []);

    // Handle OAuth return
    useEffect(() => {
        if (sp.get("google_connected") === "1") {
            toast.success("Google Calendar connected");
            sp.delete("google_connected");
            setSp(sp, { replace: true });
        } else if (sp.get("google_error")) {
            const err = sp.get("google_error");
            toast.error(`Couldn't connect Google Calendar: ${decodeURIComponent(err)}`, { duration: 12000 });
            sp.delete("google_error");
            setSp(sp, { replace: true });
        }
    }, [sp, setSp]);

    const toggle = async (val) => {
        setBusy(true);
        try {
            await api.put("/settings/reminder-guard", { reminder_guard_enabled: val });
            toast.success(val ? "Live emails ENABLED" : "Dry-run mode — real customers safe");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    const run = async () => {
        setRunning(true);
        try {
            const r = await api.post("/reminders/run");
            setLastRun(r.data);
            toast.success(
                `Scan complete — ${r.data.sent.length} sent, ${r.data.suppressed.length} suppressed`,
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Scan failed");
        } finally {
            setRunning(false);
        }
    };

    const connectGoogle = async () => {
        setGBusy(true);
        try {
            const r = await api.get("/google/authorize");
            window.location.href = r.data.authorization_url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start Google auth");
            setGBusy(false);
        }
    };

    const pickCalendar = async (calendarId) => {
        const cal = calendars.find((c) => c.id === calendarId);
        setGBusy(true);
        try {
            await api.put("/google/calendar", {
                calendar_id: calendarId,
                calendar_name: cal?.name || "",
            });
            toast.success(`Syncing appointments to "${cal?.name}"`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setGBusy(false);
        }
    };

    const disconnectGoogle = async () => {
        if (!confirm("Disconnect Google Calendar? Existing events stay put, but new appointments won't sync.")) return;
        setGBusy(true);
        try {
            await api.post("/google/disconnect");
            toast.success("Google Calendar disconnected");
            setCalendars([]);
            load();
        } catch {
            toast.error("Failed");
        } finally {
            setGBusy(false);
        }
    };

    const syncFromGoogle = async () => {
        setGBusy(true);
        try {
            const r = await api.post("/google/sync-now");
            if (r.data.ok === false) {
                toast.error(
                    r.data.reason === "not_connected"
                        ? "Google Calendar not connected"
                        : `Sync failed (${r.data.reason})`,
                );
            } else {
                const u = r.data.updated?.length || 0;
                const c = r.data.cancelled?.length || 0;
                if (u === 0 && c === 0) toast.success("Already up to date");
                else toast.success(`Synced from Google — ${u} moved, ${c} cancelled`);
            }
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sync failed");
        } finally {
            setGBusy(false);
        }
    };

    const guardOn = !!settings?.reminder_guard_enabled;

    return (
        <div className="space-y-6" data-testid="reminders-page">
            <div>
                <p className="text-label">Automation</p>
                <h2 className="font-display font-black text-4xl uppercase tracking-tight leading-none">
                    Reminders
                </h2>
                <p className="text-neutral-600 mt-2 max-w-2xl">
                    The scan runs every morning at 08:00 UK time. It emails any customer whose next service
                    date is within 30 days. Toggle live mode below when you’re happy to start sending real
                    reminders — until then, only the test address (
                    <span className="font-mono">{settings?.test_reminder_email}</span>) receives.
                </p>
            </div>

            <div
                className="dh-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5"
                style={{
                    borderColor: guardOn ? "var(--dh-red)" : "var(--dh-line)",
                    boxShadow: guardOn
                        ? "6px 6px 0 var(--dh-red)"
                        : "4px 4px 0 var(--dh-line)",
                }}
                data-testid="reminder-guard-card"
            >
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        {guardOn ? (
                            <AlertTriangle className="text-[color:var(--dh-red)]" size={20} />
                        ) : (
                            <ShieldCheck className="text-[color:var(--dh-green)]" size={20} />
                        )}
                        <p
                            className={`text-label ${guardOn ? "!text-[color:var(--dh-red)]" : "!text-[color:var(--dh-green)]"}`}
                        >
                            {guardOn ? "LIVE — customers receive emails" : "DRY RUN — customers muted"}
                        </p>
                    </div>
                    <h3 className="font-display font-black text-2xl uppercase leading-tight">
                        Reminder guard
                    </h3>
                    <p className="text-sm text-neutral-600 mt-2 max-w-lg">
                        Flip this on when the email content is signed off. It’s the one gate between the daily
                        scan and 36 real inboxes.
                    </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <Switch
                        data-testid="reminder-guard-toggle"
                        checked={guardOn}
                        onCheckedChange={toggle}
                        disabled={busy}
                        className="scale-150"
                    />
                    <span className="text-[10px] tracking-widest uppercase font-bold">
                        {guardOn ? "ON" : "OFF"}
                    </span>
                </div>
            </div>

            <div className="dh-card p-5 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1">
                        <p className="text-label">Manual trigger</p>
                        <h3 className="font-display font-black text-xl uppercase leading-tight">
                            Run scan now
                        </h3>
                        <p className="text-sm text-neutral-600 mt-1">
                            Sends reminders for jobs due within 30 days that haven’t been reminded yet.
                        </p>
                    </div>
                    <button
                        onClick={run}
                        disabled={running}
                        className="dh-btn"
                        data-testid="run-scan-btn"
                        data-variant="dark"
                    >
                        <Play size={16} /> {running ? "Running…" : "Run scan"}
                    </button>
                </div>

                {lastRun && (
                    <div className="mt-5 border-t-2 border-neutral-200 pt-4" data-testid="scan-result">
                        <p className="text-label">Last run · {new Date(lastRun.run_at).toLocaleString("en-GB")}</p>
                        <div className="grid sm:grid-cols-4 gap-3 mt-3">
                            <div className="dh-card p-3">
                                <p className="text-label">Guard</p>
                                <p className="font-display font-black text-xl">
                                    {lastRun.guard_enabled ? "LIVE" : "DRY"}
                                </p>
                            </div>
                            <div className="dh-card p-3">
                                <p className="text-label">Scanned</p>
                                <p className="font-display font-black text-xl">{lastRun.scanned}</p>
                            </div>
                            <div className="dh-card p-3">
                                <p className="text-label">Sent</p>
                                <p className="font-display font-black text-xl text-[color:var(--dh-green)]">
                                    {lastRun.sent.length}
                                </p>
                            </div>
                            <div className="dh-card p-3">
                                <p className="text-label">Suppressed</p>
                                <p className="font-display font-black text-xl text-[color:var(--dh-amber)]">
                                    {lastRun.suppressed.length}
                                </p>
                            </div>
                        </div>
                        {lastRun.sent.length > 0 && (
                            <div className="mt-3">
                                <p className="text-label mb-1">Sent to</p>
                                <ul className="text-sm font-mono space-y-0.5">
                                    {lastRun.sent.map((s) => (
                                        <li key={s.job_id}>→ {s.to}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {lastRun.failed?.length > 0 && (
                            <div className="mt-3">
                                <p className="text-label mb-1 !text-[color:var(--dh-red)]">Failed</p>
                                <ul className="text-sm font-mono space-y-0.5">
                                    {lastRun.failed.map((s) => (
                                        <li key={s.job_id}>× {s.error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="dh-card p-4 flex items-start gap-3">
                <ShieldOff className="text-neutral-500 mt-0.5" size={18} />
                <p className="text-sm text-neutral-700">
                    Every job also carries a <span className="font-mono">reminder_sent</span> flag so no
                    customer ever gets the same reminder twice, even if the scan is re-run.
                </p>
            </div>

            {/* ---- Google Calendar sync ---- */}
            <div className="dh-card p-5 sm:p-6" data-testid="google-calendar-card">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <CalendarCheck className="text-[color:var(--dh-blue)]" size={20} />
                            <p className="text-label !text-[color:var(--dh-blue)]">Calendar sync</p>
                        </div>
                        <h3 className="font-display font-black text-2xl uppercase leading-tight">
                            Google Calendar
                        </h3>
                        <p className="text-sm text-neutral-600 mt-1 max-w-lg">
                            Booked appointments drop straight onto a Google Calendar. If Hayden has that
                            account added on his Samsung phone, they show up in Samsung Calendar
                            automatically.
                        </p>
                    </div>

                    {google?.connected ? (
                        <div className="flex flex-col items-start sm:items-end gap-2 text-sm">
                            <p className="text-label !text-[color:var(--dh-green)]">Connected</p>
                            <p className="font-mono">{google.connected_email}</p>
                        </div>
                    ) : (
                        <button
                            className="dh-btn"
                            data-variant="dark"
                            onClick={connectGoogle}
                            disabled={gBusy}
                            data-testid="google-connect-btn"
                        >
                            <Link2 size={16} /> {gBusy ? "Redirecting…" : "Connect Google Calendar"}
                        </button>
                    )}
                </div>

                {google?.connected && (
                    <div className="mt-5 border-t-2 border-neutral-200 pt-4 space-y-4">
                        <div>
                            <label className="text-label block mb-1">Calendar to sync to</label>
                            <Select
                                value={google.calendar_id || ""}
                                onValueChange={pickCalendar}
                                disabled={gBusy}
                            >
                                <SelectTrigger
                                    className="dh-input rounded-none border-2 border-[color:var(--dh-line)]"
                                    data-testid="google-calendar-select"
                                >
                                    <SelectValue placeholder="Pick a calendar…" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                                    {calendars.map((c) => (
                                        <SelectItem key={c.id} value={c.id} data-testid={`google-cal-${c.id}`}>
                                            {c.name} {c.primary ? "· primary" : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {google.calendar_name && (
                                <p className="text-xs text-neutral-500 mt-2 font-mono">
                                    <CalendarClock size={12} className="inline mr-1" />
                                    Currently syncing to: <strong>{google.calendar_name}</strong>
                                </p>
                            )}
                        </div>
                        <div
                            className="border-2 border-neutral-200 bg-neutral-50 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                            data-testid="google-sync-health"
                        >
                            <div className="flex-1 min-w-0">
                                <p className="text-label">Sync health</p>
                                {google?.last_pull_at ? (
                                    <p className="text-sm mt-1">
                                        <span
                                            className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${
                                                google.last_pull_ok
                                                    ? "bg-emerald-500"
                                                    : "bg-red-500"
                                            }`}
                                        />
                                        <span className="font-mono text-xs">
                                            {new Date(google.last_pull_at).toLocaleString("en-GB", {
                                                day: "2-digit",
                                                month: "short",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </span>
                                        {" · "}
                                        {google.last_pull_ok ? (
                                            <span data-testid="google-sync-ok">
                                                {(google.last_pull_updated || 0) === 0 &&
                                                (google.last_pull_cancelled || 0) === 0
                                                    ? "no changes"
                                                    : `${google.last_pull_updated || 0} moved, ${google.last_pull_cancelled || 0} cancelled`}
                                            </span>
                                        ) : (
                                            <span
                                                className="text-red-700 font-bold"
                                                data-testid="google-sync-error"
                                            >
                                                {google.last_pull_error || "error"}
                                            </span>
                                        )}
                                    </p>
                                ) : (
                                    <p className="text-sm text-neutral-500 mt-1" data-testid="google-sync-never">
                                        Not run yet — click Sync now to prime it.
                                    </p>
                                )}
                            </div>
                            <button
                                className="dh-btn"
                                data-variant="ghost"
                                onClick={syncFromGoogle}
                                disabled={gBusy}
                                data-testid="google-sync-now-btn"
                            >
                                <RefreshCw size={14} className={gBusy ? "animate-spin" : ""} /> Sync now
                            </button>
                        </div>
                        <button
                            className="dh-btn"
                            data-variant="ghost"
                            onClick={disconnectGoogle}
                            disabled={gBusy}
                            data-testid="google-disconnect-btn"
                        >
                            <Unlink size={14} /> Disconnect
                        </button>
                        <p className="text-xs text-neutral-500 mt-1">
                            Two-way sync: the app checks Google every 5 minutes for
                            moved or cancelled appointments and updates the diary.
                            Changes trigger a customer email only when the reminder
                            guard is ON (or the customer is on the test whitelist).
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Reminders;
